
import { Vue, Component, Ref } from 'vue-property-decorator';
import NavLogo from '@/features/common/components/navbar/NavLogo.vue';
import { requiredRules, charactersLimitRules } from '@/features/common/helpers/validationRules';
import { VForm } from '@/features/common/types';
import { showErrorAlertDialog } from '@/features/common/helpers';
import { getCurrentLoggedInUser, saveToken } from '../helpers';
import { fetchConfirmStatus, sendEmailConfirmationCode, validateEmailConfirmationCode } from '../services/auth.services';
import { ConfirmationCodeEnum } from '../types';

@Component({
  components: {
    NavLogo,
  },
})
export default class SignInEmailConfirm extends Vue {
  @Ref('formEmailConfirm') formEmailConfirm!: VForm;

  private requiredRules = requiredRules;

  private charactersLimitRules = charactersLimitRules;

  private data = {
    email: '',
    confirmationCode: '',
  }

  private error = {
    show: false,
    message: 'Confirmation code is invalid. Please try again!',
  };

  private resendButton = {
    loading: false,
    text: 'Resend Code',
  }

  private attempts = 0;

  private maxAttempts = 3;

  get confirmationCodeType(): ConfirmationCodeEnum {
    return this.$route.query.codeType as ConfirmationCodeEnum || ConfirmationCodeEnum.TwoFA;
  }

  created(): void {
    this.$store.commit('setLoading', false);

    const currentLoggedInUser = getCurrentLoggedInUser();
    if (!currentLoggedInUser) {
      this.signInAgain();
      return;
    }

    const { email } = currentLoggedInUser;

    this.data.email = email;

    this.fetchAndSetConfirmStatus();
  }

  private async fetchAndSetConfirmStatus(): Promise<void> {
    if (!this.data.email) return;

    const response = await fetchConfirmStatus(this.data.email, this.confirmationCodeType);

    const { successful, data } = response || {};

    if (!successful) return;

    this.attempts = data?.attempts || 0;
    this.maxAttempts = data?.maxAttempts || 3;

    if (this.attempts >= this.maxAttempts) {
      this.signInAgain('You have reached your maximum attempts. Try log into your account again.');
    }
  }

  private async submit(): Promise<void> {
    this.error.show = false;

    const isValid = this.formEmailConfirm.validate();
    if (!isValid) return;

    const { email } = this.data;

    this.$store.commit('setLoading', true);
    const response = await validateEmailConfirmationCode(
      { email, code: this.data.confirmationCode.trim() },
      this.confirmationCodeType,
    );
    this.$store.commit('setLoading', false);

    const { successful, tokenData, errorType } = response || {};

    if (successful && tokenData) {
      saveToken(tokenData);
      this.$router.replace({ name: 'dashboard' });
      return;
    }

    if (['ConfirmationCodeExpired', 'MaxCodeAttemptsReached'].includes(errorType || '')) {
      this.signInAgain(response.errorMessage);
      return;
    }

    if (errorType === 'InvalidConfirmationCode') {
      this.attempts += 1;

      if (this.attempts >= this.maxAttempts) {
        this.signInAgain('You have reached your maximum attempts. Try log into your account again.');
        return;
      }
    }

    this.error = {
      show: true,
      message: response.errorMessage || 'Confirmation code is invalid. Please try again!',
    };
  }

  private async resendCode(): Promise<void> {
    this.error.show = false;
    this.attempts = 0;

    const { email } = this.data;

    this.resendButton.loading = true;
    const response = await sendEmailConfirmationCode(email, this.confirmationCodeType);
    this.resendButton.loading = false;

    if (response.successful) {
      this.resendButton.text = 'Code Sent!';
      setTimeout(() => {
        this.resendButton.text = 'Resend Code';
      }, 2000);
      return;
    }

    this.error = {
      show: true,
      message: response.errorMessage || 'Couldn\'t send confirmation code. Please try again!',
    };
  }

  private signInAgain(errorMessage?: string) {
    if (errorMessage) {
      showErrorAlertDialog(errorMessage);
    }

    this.$store.dispatch('logout');
    this.$router.push({ name: 'sign-in' });
  }
}
